import { apibooKey, getStorageNamespace, onStorageNamespaceChange } from '../services/storageNamespace';
import { appendTabStripEntry } from '../utils/workspaceTabPersistence';
import { getLastActiveMap, setLastActiveMap, tabToShowFor, currentDocWorkspaceKey } from './documentTabWorkspaces';
import { setActiveDocumentTabId, setDocumentTabs, setTabStripOrder, useTabsStore } from './tabsStore';

/**
 * Open document tabs survive a restart (owner, 14.10.2026): every workspace's
 * open tabs, their strip order and the tab that was active in each. One small
 * JSON per ACCOUNT (`apiboo-<userId>-documents-open-tabs`, kept at sign-out
 * like the account's other on-device keys, unreachable from another account).
 *
 * Writes are skipped until the current namespace has been read back, so the
 * logout `reset()` — which runs after the namespace already moved to `_local`
 * — can never overwrite the signed-out device's own saved tabs with nothing.
 */
const SUFFIX = 'documents-open-tabs';

let loadedNs = null;
let lastWritten = null;

const keyFor = (ns) => apibooKey(SUFFIX, ns);

function orderedDocTabs(state) {
  const byId = new Map(state.documentTabs.map((tab) => [tab.id, tab]));
  const ordered = [];
  for (const e of state.tabStripOrder || []) {
    if (e.type === 'document-editor' && byId.has(e.id)) {
      ordered.push(byId.get(e.id));
      byId.delete(e.id);
    }
  }
  return [...ordered, ...byId.values()];
}

export function serializeDocumentTabs(state, lastActive) {
  const tabs = orderedDocTabs(state).map(({ id, documentId, label, scope }) => ({ id, documentId, label, scope }));
  const ids = new Set(tabs.map((tab) => tab.id));
  const active = {};
  for (const [key, id] of Object.entries(lastActive || {})) if (ids.has(id)) active[key] = id;
  return { v: 1, tabs, active };
}

function save() {
  const ns = getStorageNamespace();
  if (loadedNs == null || ns !== loadedNs) return;
  const json = JSON.stringify(serializeDocumentTabs(useTabsStore.getState(), getLastActiveMap()));
  if (json === lastWritten) return;
  lastWritten = json;
  try {
    localStorage.setItem(keyFor(ns), json);
  } catch {
    /* storage full / unavailable — tabs then last for this session */
  }
}

function read(ns) {
  try {
    const parsed = JSON.parse(localStorage.getItem(keyFor(ns)) || 'null');
    return parsed && Array.isArray(parsed.tabs) ? parsed : null;
  } catch {
    return null;
  }
}

/** Merge the saved tabs of `ns` into the store (tabs opened meanwhile stay). */
export function hydrateDocumentTabs(ns = getStorageNamespace()) {
  const saved = read(ns);
  if (saved?.tabs?.length) {
    const open = new Set(useTabsStore.getState().documentTabs.map((tab) => tab.id));
    const restored = saved.tabs
      .filter((tab) => tab?.id && tab.documentId && !open.has(tab.id))
      .map((tab) => ({ id: tab.id, type: 'document-editor', documentId: tab.documentId, label: tab.label || 'Untitled', scope: tab.scope || { type: 'personal' } }));
    if (restored.length) {
      setLastActiveMap({ ...(saved.active || {}), ...getLastActiveMap() });
      setDocumentTabs((prev) => [...prev, ...restored]);
      setTabStripOrder((prev) =>
        restored.reduce((order, tab) => appendTabStripEntry(order, { type: 'document-editor', id: tab.id }), prev)
      );
      const state = useTabsStore.getState();
      if (!state.activeDocumentTabId) setActiveDocumentTabId(tabToShowFor(state, currentDocWorkspaceKey()));
    }
  }
  loadedNs = ns;
  lastWritten = null;
  save();
}

let installed = false;

/** Restore now, save on every document-tab change, re-read on an account switch. */
export function installDocumentTabsPersist() {
  if (installed) return;
  installed = true;
  hydrateDocumentTabs();
  useTabsStore.subscribe((state, prev) => {
    if (
      state.documentTabs !== prev.documentTabs ||
      state.activeDocumentTabId !== prev.activeDocumentTabId ||
      state.tabStripOrder !== prev.tabStripOrder
    ) {
      save();
    }
  });
  onStorageNamespaceChange((ns) => {
    loadedNs = null;
    setLastActiveMap({});
    // After the switch's own teardown (logout runs tabsStore.reset() right after).
    setTimeout(() => hydrateDocumentTabs(ns), 0);
  });
}
